/**
 * UIController类 - 用户界面交互控制
 * 
 * 职责:
 * - 绑定按钮点击事件
 * - 响应触摸和鼠标事件
 * - 显示性能统计信息
 * - 适配移动端布局
 * 
 * 验证需求: Requirements 2.1, 2.2, 2.3, 3.1, 3.3
 */
class UIController {
  constructor(app) {
    if (!app) {
      throw new Error('App instance is required');
    }

    this.app = app;
    
    // UI元素
    this.btnSingle = null;
    this.btnBurst5 = null;
    this.btnBurst10 = null;
    this.statFPS = null;
    this.statParticles = null;
    this.uiPanel = null;
    
    // 防抖
    this.lastClickTime = 0;
    this.clickDebounceDelay = 100; // 100ms
    
    // 检测移动设备
    this.isMobile = this._detectMobile();
  }

  /**
   * 检测是否为移动设备
   * @returns {boolean}
   * @private
   */
  _detectMobile() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           window.innerWidth < 768;
  }

  /**
   * 初始化UI控制器
   */
  init() {
    // 获取UI元素
    this.btnSingle = document.getElementById('btn-single');
    this.btnBurst5 = document.getElementById('btn-burst-5');
    this.btnBurst10 = document.getElementById('btn-burst-10');
    this.statFPS = document.getElementById('stat-fps');
    this.statParticles = document.getElementById('stat-particles');
    this.uiPanel = document.getElementById('ui-panel');
    
    // 检查元素是否存在
    if (!this.btnSingle || !this.btnBurst5 || !this.btnBurst10) {
      console.error('UI buttons not found');
      return;
    }
    
    // 绑定事件
    this.bindEvents();
    
    // 适配移动端
    if (this.isMobile) {
      this._adaptMobileLayout();
    }
  }

  /**
   * 绑定事件
   */
  bindEvents() {
    // 单发按钮
    if (this.btnSingle) {
      this.btnSingle.addEventListener('click', () => this.onSingleLaunch());
    }
    
    // 五连发按钮
    if (this.btnBurst5) {
      this.btnBurst5.addEventListener('click', () => this.onBurstLaunch(5));
    }
    
    // 十连发按钮
    if (this.btnBurst10) {
      this.btnBurst10.addEventListener('click', () => this.onBurstLaunch(10));
    }
    
    // 监听窗口大小变化
    window.addEventListener('resize', () => this._handleResize());
  }

  /**
   * 单发按钮点击处理
   */
  onSingleLaunch() {
    if (!this._canClick()) return;
    
    // 调用应用的发射方法
    if (this.app && this.app.launchSingle) {
      this.app.launchSingle();
    }
    
    // 添加视觉反馈
    this._addButtonFeedback(this.btnSingle);
  }

  /**
   * 连发按钮点击处理
   * @param {number} count - 烟花数量
   */
  onBurstLaunch(count) {
    if (!this._canClick()) return;
    
    // 调用应用的连发方法
    if (this.app && this.app.launchBurst) {
      this.app.launchBurst(count);
    }
    
    // 添加视觉反馈
    const button = count === 5 ? this.btnBurst5 : this.btnBurst10;
    this._addButtonFeedback(button);
  }

  /**
   * 检查是否可以点击（防抖）
   * @returns {boolean}
   * @private
   */
  _canClick() {
    const now = Date.now();
    if (now - this.lastClickTime < this.clickDebounceDelay) {
      return false;
    }
    this.lastClickTime = now;
    return true;
  }

  /**
   * 添加按钮点击反馈
   * @param {HTMLElement} button - 按钮元素
   * @private
   */
  _addButtonFeedback(button) {
    if (!button) return;
    
    // 添加active类
    button.classList.add('active');
    
    // 200ms后移除
    setTimeout(() => {
      button.classList.remove('active');
    }, 200);
  }

  /**
   * 更新FPS显示
   * @param {number} fps - 当前FPS
   */
  updateFPS(fps) {
    if (this.statFPS) {
      this.statFPS.textContent = Math.round(fps);
      
      // 根据FPS设置颜色
      if (fps < 25) {
        this.statFPS.style.color = '#ff4444'; // 红色
      } else if (fps < 40) {
        this.statFPS.style.color = '#ffaa00'; // 橙色
      } else {
        this.statFPS.style.color = '#44ff44'; // 绿色
      }
    }
  }

  /**
   * 更新粒子数量显示
   * @param {number} count - 当前粒子数量
   */
  updateParticleCount(count) {
    if (this.statParticles) {
      this.statParticles.textContent = count;
    }
  }

  /**
   * 适配移动端布局
   * @private
   */
  _adaptMobileLayout() {
    if (this.uiPanel) {
      this.uiPanel.classList.add('mobile');
    }
    
    // 增大按钮尺寸（最小44x44px）
    const buttons = [this.btnSingle, this.btnBurst5, this.btnBurst10];
    buttons.forEach(btn => {
      if (btn) {
        btn.style.minWidth = '44px';
        btn.style.minHeight = '44px';
      }
    });
  }

  /**
   * 处理窗口大小变化
   * @private
   */
  _handleResize() {
    const wasMobile = this.isMobile;
    this.isMobile = this._detectMobile();
    
    // 如果设备类型改变，重新适配布局
    if (wasMobile !== this.isMobile) {
      if (this.isMobile) {
        this._adaptMobileLayout();
      } else {
        if (this.uiPanel) {
          this.uiPanel.classList.remove('mobile');
        }
      }
    }
  }

  /**
   * 显示错误消息
   * @param {string} message - 错误消息
   */
  showError(message) {
    const errorElement = document.getElementById('error-message');
    const errorText = document.getElementById('error-text');
    
    if (errorElement && errorText) {
      errorText.textContent = message;
      errorElement.classList.remove('hidden');
      
      // 5秒后自动隐藏
      setTimeout(() => {
        errorElement.classList.add('hidden');
      }, 5000);
    }
  }

  /**
   * 隐藏错误消息
   */
  hideError() {
    const errorElement = document.getElementById('error-message');
    if (errorElement) {
      errorElement.classList.add('hidden');
    }
  }

  /**
   * 启用/禁用按钮
   * @param {boolean} enabled - 是否启用
   */
  setButtonsEnabled(enabled) {
    const buttons = [this.btnSingle, this.btnBurst5, this.btnBurst10];
    buttons.forEach(btn => {
      if (btn) {
        btn.disabled = !enabled;
      }
    });
  }

  /**
   * 获取是否为移动设备
   * @returns {boolean}
   */
  getIsMobile() {
    return this.isMobile;
  }
}

export default UIController;
